import { Address, AuctionState, BiddingScheduleRange } from "@prisma/client";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  BiddifyPaymentMethod,
  isLink,
  MinimalCard,
} from "./interface/stripe/BiddifyPaymentMethod";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function transformEmptyStringsToNull(obj: any) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] === "") {
      obj[key] = null;
    }
  }
  return obj;
}

export function transformNullToEmptyStrings(obj: any) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] === null) {
      obj[key] = "";
    }
  }
  return obj;
}

// Return elements of A which aren't in B
export function difference<T>(arrayA: T[], arrayB: T[]): T[] {
  return arrayA.filter((element) => !arrayB.includes(element));
}

export const parseBoolean = (str: string | null, _default = true) => {
  if (!str) return _default;

  switch (str.toLowerCase()) {
    case "true":
    case "yes":
      return true;
    case "false":
    case "no":
      return false;
    default:
      return _default;
  }
};

export const formatCurrency = (
  amount: number | null,
  showComma: boolean = true,
  decimalPlaces: number | undefined = 0
) => {
  if (amount === null) return amount;

  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: decimalPlaces,
    useGrouping: showComma,
  }).format(amount);
};

export function decimalToPercent(decimal: number) {
  // Convert the decimal to a percentage
  let percent = decimal * 100;

  // Convert to string and add the percent sign
  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
}

export const generateImageUrlFromKey = (imageKey: string | undefined) => {
  if (typeof imageKey === "undefined" || imageKey === null) {
    return "";
  }

  return (
    `https://s3.${process.env.NEXT_PUBLIC_AWS_REGION}` +
    `.amazonaws.com/${process.env.NEXT_PUBLIC_AWS_IMAGE_BUCKET}` +
    `/${imageKey}`
  );
};

export const getBaseUrl = () => {
  return process.env.NEXT_PUBLIC_BIDDIFY_ROOT_URL
    ? "https://" + process.env.NEXT_PUBLIC_BIDDIFY_ROOT_URL
    : "http://localhost:3000";
};

export const ordinal = (day: number) => {
  if (day > 3 && day < 21) return "th"; // For dates like 4th, 5th, 6th, etc.
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const chunkArray = (array: any[], chunkSize: number) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    chunks.push(chunk);
  }
  return chunks;
};

export const calculateBidIncrement = ({
  currentBid,
  ranges,
}: {
  currentBid: number;
  ranges: BiddingScheduleRange[];
}) => {
  // If the currentBid is higher than the last range, simply
  // repeat the increment in the highest range.
  let lastIncrement = Number(ranges[ranges.length - 1].increment);

  for (const range of ranges) {
    if (Number(range.rangeEnd) >= currentBid) return Number(range.increment);
    lastIncrement = Number(range.increment);
  }

  return lastIncrement;
};

// Precondition: ranges are sorted by rangeEnd.
export const getMaxAllowableBid = (ranges: BiddingScheduleRange[]) => {
  return ranges[ranges.length - 1]?.rangeEnd;
};

export const truncateString = (str: string, maxLength: number) => {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength - 3) + "...";
};

export function getAuctionTimeString(
  auctionState: AuctionState,
  auctionStart: Date,
  timeLeft: number | undefined
) {
  // timeLeft is in milliseconds
  return (auctionState === AuctionState.PREVIEW ||
    auctionState === AuctionState.CREATING)
    ? getTimeUntilString(auctionStart)
    : formatTimeLeft(timeLeft);
}

export function debugTimestamp() {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // JS months are zero-based
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const milliseconds = String(now.getMilliseconds()).padStart(3, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function compareTimes(time1: string, time2: string): number {
  // Helper function to convert time string to minutes since midnight
  const timeToMinutes = (time: string): number => {
    let [timePart, meridian] = time.split(/(AM|PM)/i);
    let [hours, minutes] = timePart.split(":").map(Number);

    // Convert 12-hour format to 24-hour
    if (meridian.toUpperCase() === "PM" && hours < 12) {
      hours += 12;
    } else if (meridian.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  };

  // Convert both times to minutes since midnight
  const minutes1 = timeToMinutes(time1);
  const minutes2 = timeToMinutes(time2);
  // Compare the two times
  if (minutes1 < minutes2) {
    return -1; // time1 is earlier
  } else if (minutes1 > minutes2) {
    return 1; // time1 is later
  } else {
    return 0; // times are equal
  }
}

export function combineDateTime(
  date: Date,
  time: string,
  meridian: "AM" | "PM"
) {
  let [hours, minutes] = time.split(":").map(Number);
  if (meridian === "PM" && hours < 12) {
    hours += 12;
  } else if (meridian === "AM" && hours === 12) {
    hours = 0;
  }
  const dateTime = new Date(date);
  dateTime.setHours(hours, minutes);
  return dateTime;
}

export function formatList(items: string[]) {
  // Check if the list is empty
  if (items.length === 0) {
    return "";
  }
  // If there is only one item, return it
  if (items.length === 1) {
    return items[0];
  }
  // If there are two items, join them with "and"
  if (items.length === 2) {
    return items.join(" and ");
  }
  // For three or more items, use the Oxford comma
  const lastItem = items.pop();
  return `${items.join(", ")}, and ${lastItem}`;
}

export function capitalizeFirstLetter(str: string) {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatPhoneNumber(phoneNumber: string | null) {
  if (!phoneNumber) return "";

  const parsedNumber = parsePhoneNumberFromString(phoneNumber);
  if (!parsedNumber) {
    return "";
  }

  return parsedNumber.formatInternational();
}

export function toTitleCase(str?: string) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, function (t) {
    return t.toUpperCase();
  });
}

export function constructFullName(
  name: string | null | undefined,
  lastName: string | null | undefined
) {
  if (name && lastName) {
    return `${name} ${lastName}`;
  } else if (name) {
    return name;
  } else {
    return "";
  }
}

export function getNewLotEndDate(oldEndDate: Date) {
  const now = new Date();
  const twoMinutesLater = new Date(now.getTime() + 2 * 60 * 1000); // Two minutes from now
  return new Date(Math.max(twoMinutesLater.getTime(), oldEndDate.getTime()));
}

export function hasDuplicates<T>(arr: T[]): boolean {
  return new Set(arr).size < arr.length;
}

export const isCreditCardExpired = (card: BiddifyPaymentMethod): boolean => {
  if (isLink(card)) return false;

  const { exp_month, exp_year } = card;

  if (!exp_month || !exp_year) return true;

  // Determine if the card is expired
  const currentDate = new Date();
  const expirationDate = new Date(exp_year, exp_month - 1);
  return expirationDate < currentDate;
};

export const fetcher = async (url: string) => {
  return fetcherWithOptions(url);
};

export const fetcherWithOptions = async (
  url: string,
  options?: RequestInit
) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error("An error occurred while fetching the data");
  }
  // Handle no-content responses gracefully for consumers like SWR
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

// Determine if a string is alphabetical or whitespace
export function getValidSubstring(str: string) {
  const match = str.match(/^[a-zA-Z0-9\s,&]*/);
  return match ? match[0] : "";
}

export function getFirstImageInAuction(
  auctionLots: { [key: string]: { key: string }[] }[]
) {
  for (let lot of auctionLots) {
    if (lot.Image[0] && lot.Image[0].key) {
      return lot.Image[0].key;
    }
  }
  return null;
}

// TODO: Remove

export function getDaySuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

// TODO: Remove

// Format time ago on lot bid history.
export function timeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals = [
    { label: "Just now", seconds: 60 },
    { label: "m ago", seconds: 60 * 60 },
    { label: "h ago", seconds: 24 * 60 * 60 },
    { label: "Yesterday", seconds: 48 * 60 * 60 },
    { label: "days ago", seconds: 30 * 24 * 60 * 60 },
  ];

  for (const interval of intervals) {
    if (seconds < interval.seconds) {
      if (interval.label === "Just now") {
        return interval.label;
      } else if (interval.label === "Yesterday") {
        return interval.label;
      } else if (interval.label === "days ago") {
        const days = Math.floor(seconds / (24 * 60 * 60));
        return `${days} ${interval.label}`;
      } else if (interval.label === "h ago") {
        const hours = Math.floor(seconds / (60 * 60));
        return `${hours}${interval.label}`;
      } else if (interval.label === "m ago") {
        const minutes = Math.floor(seconds / 60);
        return `${minutes}${interval.label}`;
      }
    }
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const suffix = getDaySuffix(day);
  return `${month} ${day}${suffix}`;
}

import { Readable } from "stream";
import { formatTimeLeft, getTimeUntilString } from "./date/date";
import { formatPostalCode } from "./bidding/util";
import { EmailAction } from "./enum/emailTypes";
import { generateJwtToken } from "./auth";

export function streamToBuffer(readableStream: Readable): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    readableStream.on("data", (chunk) => {
      chunks.push(chunk);
    });

    readableStream.on("end", () => {
      resolve(Buffer.concat(chunks));
    });

    readableStream.on("error", (err) => {
      reject(err);
    });
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  "use client";
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function scrollTop(): void {
  scrollTo({ top: 0, left: 0, behavior: "smooth" });
}

export function deepEqual(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const parseCategories = (categoryFilter?: string | null) => {
  return categoryFilter
    ? categoryFilter.split(",").map((categoryFilter) => categoryFilter.trim())
    : [];
};

export const parseBudget = (
  budgetFilter?: string | null
): { min?: number; max?: number } => {
  if (!budgetFilter) return {};

  const aboveMatch = budgetFilter.match(/above \$(\d+)/);
  if (aboveMatch) {
    const minValue = parseInt(aboveMatch[1], 10);
    return { min: minValue };
  }

  const belowMatch = budgetFilter.match(/below \$(\d+)/);
  if (belowMatch) {
    const maxValue = parseInt(belowMatch[1], 10);
    return { max: maxValue };
  }

  const rangeMatch = budgetFilter.match(/\$(\d+) to \$(\d+)/);
  if (rangeMatch) {
    const minValue = parseInt(rangeMatch[1], 10);
    const maxValue = parseInt(rangeMatch[2], 10);
    return { min: minValue, max: maxValue };
  }

  return {};
};

export const constructBudgetLabel = (
  budgetMin: string | undefined,
  budgetMax: string | undefined
) => {
  let label;

  if (budgetMin?.length && budgetMax?.length) {
    label = `Budget: $${budgetMin} to $${budgetMax}`;
  } else if (budgetMin?.length) {
    label = `Budget: above $${budgetMin}`;
  } else if (budgetMax?.length) {
    label = `Budget: below $${budgetMax}`;
  }

  return label;
};

export const getTenantLogoUri = () => {
  return `https://s3.us-east-1.amazonaws.com/biddify.tenant.assets/${process.env.NEXT_PUBLIC_TENANT_ID}/logo.png`;
};

export const getImageUri = (imageKey: string | undefined) => {
  if (!imageKey) return "";
  return `https://s3.amazonaws.com/${process.env.NEXT_PUBLIC_AWS_IMAGE_BUCKET}/${imageKey}`;
};

export const toNANP = (phoneNumber?: string | null): string | null => {
  if (!phoneNumber) return null;

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Remove leading country code if present (e.g. +1 or 1)
  const digits = cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;

  // Return empty if not enough digits
  if (digits.length !== 10) return null;

  // Format as (XXX) XXX-XXXX
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export function twoDecimalPlaces(amount: number): number {
  return Number(amount.toFixed(2));
}

export const formatAddress = (address: Address | null) => {
  if (!address) return null
  const mainAddress = address.address2 ? (address.address1 + ' ' + address.address2) : address.address1
  return `${mainAddress}, ${address.city}, ${address.region} ${formatPostalCode(address.postalCode)}`
}

export const appendSearchParams = (href: string, searchParams: URLSearchParams) => {
  return `${href}${searchParams.size ? `?${searchParams.toString()}` : ''}`
}

export const generateEmailVerificationToken = async (
  email: string,
  expiry: string,
  type: EmailAction = EmailAction.CREATE
) => {
  const payload = {
    email: email,
    type: type,
  };

  return await generateJwtToken(payload, expiry);
};